import { MoreHorizontal } from "lucide-react";
import { z } from "zod";

import type { OrderStatusEnum } from "@/types/order";
import type { ColumnDef } from "@tanstack/react-table";

import { StatusTag } from "../order/status-tag";
import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger
} from "../ui/dropdown-menu";

function createTableHeaders(
	keys: string[],
	options: {
		capitalize?: boolean;
		customLabels?: Record<string, string>;
		excludeKeys?: string[];
	} = {}
) {
	const { capitalize = true, customLabels = {}, excludeKeys = [] } = options;

	return keys
		.filter(key => !excludeKeys.includes(key))
		.map(key => ({
			key,
			label:
				customLabels[key]
				|| (capitalize
					? key.charAt(0).toUpperCase()
					+ key
						.toLowerCase()
						.slice(1)
						.replace(/([A-Z])/g, " $1")
						.trim()
					: key)
		}));
}

function genActionsColumn<T>(): ColumnDef<T> {
	return {
		id: "actions",
		size: 20,
		enableHiding: false,
		header: () => <div>Actions</div>,
		accessorFn: (row: T) => row,
		cell: () => {
			return (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" className="h-8 w-8 p-0">
							<span className="sr-only">Open menu</span>
							<MoreHorizontal />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuLabel>Actions</DropdownMenuLabel>

						<DropdownMenuItem>View</DropdownMenuItem>
						<DropdownMenuItem>Delete</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			);
		}
	};
}
export function createTableColumns<T>(
	keys: (keyof T)[],
	options: {
		dateColumnKey?: string;
		capitalize?: boolean;
		customLabels?: Record<string, string>;
		excludeKeys?: string[];
	} = {},
	actionsColumn = genActionsColumn<T>()
): ColumnDef<T>[] {
	const headers = createTableHeaders(keys as string[], options);
	function isDate(value: string | number): boolean {
		const schema = z.coerce.date();
		const result = schema.safeParse(value);
		return result.success;
	}

	const dynamicColumns: ColumnDef<T>[] = headers.map(({ key, label }) => {
		const { dateColumnKey = "createdAt" } = options;
		const dateColumn = key.includes(dateColumnKey);
		return {
			id: key,
			accessorKey: key,
			width: 300,
			filterFn: dateColumn ? "dateRange" : "auto",
			accessorFn: (row: T) => row[key as keyof T],
			header: ({ column }) => (
				<div onPointerDown={() => column.toggleSorting()}>{label}</div>
			),
			cell: ({ row }) => {
				return key.toLocaleLowerCase().includes("status")
					? (
							<StatusTag status={row.getValue(key as OrderStatusEnum)} />
						)
					: (
							<div className="tabular-nums">
								{isDate(row.getValue(key as string))
									? new Date(row.getValue(key as string)).toLocaleDateString()
									: row.getValue(key as string)}
							</div>
						);
			}
		};
	});

	return [...dynamicColumns, actionsColumn];
}
